<template>
  <div id="inbox-container" class="h-flex">
    <aside id="contact-list">
      <div class="list-header">
        <div class="search-row">
          <div class="search-field">
            <Search :size="16" class="search-glyph" />
            <b-form-input
              id="search-inbox-input"
              v-model="filters.search"
              placeholder="Search conversations"
              type="text"
              size="sm"
              data-cy="search-inbox"
              @keyup.enter="search"
            />
            <button
              v-if="filters.search"
              class="clear-search"
              type="button"
              aria-label="Clear search"
              @click="clearSearch"
            >
              <CircleX :size="14" />
            </button>
          </div>

          <b-dropdown
            :text="selectedFieldText"
            variant="outline-secondary"
            size="sm"
            class="field-dropdown"
            right
          >
            <b-dropdown-item
              v-for="option in fieldValues"
              :key="option.value"
              :active="option.value === filters.selectedField"
              @click="setSearchField(option.value)"
            >
              {{ option.text }}
            </b-dropdown-item>
          </b-dropdown>
        </div>

        <div class="tab-row">
          <nav class="tab-strip" role="tablist">
            <button
              v-for="tab in tabs"
              :key="tab.value"
              :class="['tab', { active: activeTab === tab.value }]"
              type="button"
              role="tab"
              :aria-selected="activeTab === tab.value"
              :data-cy="`inbox-tab-${tab.value}`"
              @click="setTab(tab.value)"
              @mouseenter="onTabHover(tab)"
              @focusin="onTabHover(tab)"
            >
              <span>{{ tab.label }}</span>
              <span v-if="tab.count != null" class="tab-count">{{
                tab.count
              }}</span>
            </button>
          </nav>

          <button
            id="filter-trigger-btn"
            class="chip chip-add"
            type="button"
            data-cy="additional-filters"
            @click="displayFilters"
          >
            <Plus :size="12" />
            <span>Filter</span>
          </button>

          <b-button
            v-if="canComposeMassMessage && merchant.allow_group_message"
            variant="blue"
            size="sm"
            class="compose-btn"
            data-cy="compose"
            @click="compose"
          >
            <Pencil :size="14" />
            <span>Group message</span>
          </b-button>
        </div>

        <div class="chip-row">
          <button
            v-if="filters.location && filters.location !== 'all'"
            class="chip"
            type="button"
            @click="resetLocation"
          >
            <MapPin :size="12" />
            <span>{{ locationLabel }}</span>
            <X :size="12" class="chip-x" />
          </button>

          <button
            v-for="manager in filters.selectedAccountManagers"
            :key="manager.pkid"
            class="chip"
            type="button"
            @click="removeManager(manager)"
          >
            <User :size="12" />
            <span>{{ managerLabel(manager) }}</span>
            <X :size="12" class="chip-x" />
          </button>

          <button
            v-if="hasActiveFilters"
            class="chip chip-clear"
            type="button"
            @click="onResetFilters"
          >
            Clear all
          </button>
        </div>
      </div>

      <div class="list-scroll" tabindex="0" @keydown="onListKeydown">
        <transition-group name="contact" tag="div" class="relative-parent">
          <template v-for="group in groupedMessages">
            <div :key="`hdr-${group.key}`" class="date-header">
              {{ group.label }}
            </div>
            <button
              v-for="customerMessage of group.items"
              :key="customerMessage.customer_id"
              :class="[
                'contact-row',
                {
                  active: activeContact === customerMessage.customer_id,
                  unread: customerMessage.unread_messages > 0
                }
              ]"
              type="button"
              @click="getContact(customerMessage)"
            >
              <div class="avatar-wrap">
                <b-avatar :text="initials(customerMessage)" />
                <span
                  v-if="customerMessage.unread_messages"
                  class="presence-dot"
                  :aria-label="`${customerMessage.unread_messages} unread`"
                ></span>
              </div>

              <div class="preview">
                <div class="row-top">
                  <span class="name ellipsis">{{ name(customerMessage) }}</span>
                  <span class="time">
                    {{ rowTime(customerMessage) }}
                  </span>
                </div>
                <div class="row-bottom">
                  <span v-if="customerMessage.external_id" class="ext-id">
                    {{ customerMessage.external_id }}
                  </span>
                  <span class="preview-body">
                    {{ customerMessage.last_message_body }}
                  </span>
                </div>
              </div>
            </button>
          </template>

          <infinite-loading
            key="infinite"
            :identifier="infiniteId"
            @infinite="load"
          >
            <template #spinner>
              <div class="footer-state">Loading more…</div>
            </template>
            <template #no-more>
              <div key="end-of-conversation" class="footer-state">
                You're all caught up
              </div>
            </template>
            <template #no-results>
              <div key="no-results" class="footer-state empty">
                <MessageSquare :size="28" />
                <p>No conversations match these filters.</p>
                <button
                  v-if="hasActiveFilters"
                  type="button"
                  class="link-btn"
                  @click="onResetFilters"
                >
                  Reset filters
                </button>
              </div>
            </template>
          </infinite-loading>
        </transition-group>

        <div v-if="initialLoading" class="skeleton-overlay" aria-hidden="true">
          <div v-for="n in 7" :key="n" class="skeleton-row">
            <div class="skel skel-avatar"></div>
            <div class="skel-lines">
              <div class="skel skel-line"></div>
              <div class="skel skel-line short"></div>
            </div>
          </div>
        </div>
      </div>
    </aside>

    <Chat
      v-if="customer && customer.id"
      v-model="customer"
      :account-id="accountID()"
      :account="currentAccount"
    />
    <div v-else id="inbox-chat-placeholder" class="chat-empty">
      <div class="empty-inner">
        <img src="@/assets/messages_placeholder.svg" alt="" class="empty-art" />
        <h2>No conversation selected</h2>
        <p>Pick a customer from the list to view their message history.</p>
      </div>
    </div>

    <InboxCustomerSummary
      v-if="customer && customer.id"
      :customer="customer"
      :account="currentAccount"
      :selected-account-id="selectedAccountId"
      :merchant-id="$route.params.merchantID"
      @account-change="onAccountChange"
    />

    <ComposeMessage
      id="compose-message-modal"
      :customers="customers"
      :selected-recipients="[]"
    />

    <b-popover
      target="filter-trigger-btn"
      triggers="click"
      placement="bottomleft"
      boundary="viewport"
      custom-class="inbox-filters-popover"
      data-cy="inbox-filters"
      :show.sync="showFilterPopover"
      @shown="onFilterPopoverShown"
      @hidden="onFilterPopoverHidden"
    >
      <template #title>Filter conversations</template>

      <ValidationObserver v-slot="{ invalid }" slim>
        <b-form-group label="Location" label-for="merchantLocation">
          <ModelSelect
            id="merchantLocation"
            v-model="draftLocationOption"
            class="filter-model-select"
            data-cy="inbox-location"
            :options="locationList"
            placeholder="All Locations"
          />
        </b-form-group>

        <b-form-group label="Account Manager">
          <div class="h-flex vertical-center justify-content-between">
            <MultiUserSelect
              ref="accountManagers"
              @change="onDraftAccountManager"
            />
            <u @click="clearDraftAccountManagers">Clear</u>
          </div>
        </b-form-group>

        <div class="popover-actions">
          <b-button
            variant="outline-secondary"
            size="sm"
            data-cy="inbox-filters-cancel"
            @click="cancelFilters"
          >
            Cancel
          </b-button>
          <b-button
            variant="blue"
            size="sm"
            :disabled="invalid"
            data-cy="inbox-filters-apply"
            @click="applyFilters"
          >
            Apply
          </b-button>
        </div>
      </ValidationObserver>
    </b-popover>
  </div>
</template>

<script>
import { AuthMixin } from '@/mixins/AuthMixin/AuthMixin'
import { BAvatar } from 'bootstrap-vue'
import { Component, Mixins, Vue, Watch } from 'vue-property-decorator'
import { mapActions, mapGetters } from 'vuex'
import InfiniteLoading from 'vue-infinite-loading'
import ComposeMessage from '@/components/Modals/ComposeMessage.vue'
import VueMoment from 'vue-moment'
import {
  CircleX,
  MapPin,
  MessageSquare,
  Pencil,
  Plus,
  Search,
  User,
  X
} from 'lucide-vue'
import Chat from '@/components/Chat.vue'
import { smartInboxDateFormatter } from '@/utils/formatters'
import {
  buildInboxFilterParams,
  upsertInboxConversation
} from '@/utils/functions'
import { customerLabel } from '@/utils/platformLabels.js'
import { isAiTextsEnabled } from '@/views/settings/AiTextsView/aiTexts'
import { ModelSelect } from 'vue-search-select'
import MultiUserSelect from '@/views/MessageCenter/MultiUserSelect.vue'
import InboxCustomerSummary from '@/views/Inbox/InboxCustomerSummary.vue'
import {
  copyLocationFilterState,
  emptyInboxBuckets,
  groupRowsByDate,
  rowRecencyTimestamp,
  invalidateInboxBuckets,
  inFlight,
  nextBucketState,
  serverKeyForTab,
  tabsFor,
  visibleRowsFor
} from '@/views/Inbox/inboxTabs'

Vue.use(VueMoment)

@Component({
  components: {
    BAvatar,
    Chat,
    InboxCustomerSummary,
    InfiniteLoading,
    ComposeMessage,
    CircleX,
    MapPin,
    MessageSquare,
    ModelSelect,
    MultiUserSelect,
    Pencil,
    Plus,
    Search,
    User,
    X
  },
  computed: {
    ...mapGetters({
      merchant: 'currentMerchant',
      customer: 'currentCustomer',
      locations: 'locations'
    }),
    selectedFieldText() {
      const selectedField = this.fieldValues.find(
        option => option.value === this.filters.selectedField
      )

      return selectedField ? `By ${selectedField.text}` : 'Search by'
    }
  },
  methods: {
    ...mapActions(['getCustomer'])
  }
})
export default class Inbox extends Mixins(AuthMixin) {
  chatTimerId = null
  page_size = 25
  infiniteId = +new Date()
  activeContact = null
  customers = []
  buckets = emptyInboxBuckets()
  pendingFetch = { all: null, ai: null }
  activeContactServerKey = null
  aiPrefetchAttempted = false
  selectedAccountId = null
  defaultField = 'customer-id'
  lastSearch = ''
  initialLoading = true
  activeTab = 'all'
  // Draft filter-popover state; committed onto `filters` only by `applyFilters`.
  showFilterPopover = false
  draftLocation = ''
  draftManagers = []
  filters = {
    search: '',
    location: '',
    selectedAccountManagers: [],
    selectedField: this.defaultField
  }

  get fieldValues() {
    return [
      { text: 'Name', value: 'name' },
      {
        text: `${customerLabel(null, this.merchant)} ID`,
        value: 'customer-id'
      },
      { text: 'Phone Number', value: 'phone-number' }
    ]
  }

  async created() {
    this.setTitle()

    // TODO: Fix the event bus behavior.
    // If you load straight here and there are notifications, they
    // currently get pushed out as they load, which will mess up the data.
    this.chatTimerId = setTimeout(
      () => this.$eventBus.on('chat', this.onChat),
      5000
    )
    this.filters.location = 'all'
  }

  // Getter over the active tab's bucket; `fetchTabPage` writes to the bucket
  // directly.
  get customerMessages() {
    return this.buckets[serverKeyForTab(this.activeTab)].rows
  }

  // Reads `all` unconditionally so the Unread badge doesn't change when the
  // AI tab is active.
  get totalUnread() {
    return this.buckets.all.rows.reduce(
      (sum, m) => sum + (m.unread_messages || 0),
      0
    )
  }

  get visibleMessages() {
    return visibleRowsFor(this.activeTab, this.buckets, this.page_size)
  }

  get aiTextsEnabled() {
    return (this.locations || []).some(isAiTextsEnabled)
  }

  get tabs() {
    return tabsFor({
      allRowsCount: this.buckets.all.rows.length,
      totalUnread: this.totalUnread,
      aiTextsEnabled: this.aiTextsEnabled
    })
  }

  // Keyed on the active tab's sort field. See `rowRecencyTimestamp`.
  get groupedMessages() {
    return groupRowsByDate(this.visibleMessages, this.activeTab)
  }

  // Same field as the date headers.
  rowTime(customerMessage) {
    return smartInboxDateFormatter(
      rowRecencyTimestamp(customerMessage, this.activeTab)
    )
  }

  get hasActiveFilters() {
    return (
      !!this.filters.search ||
      (this.filters.location && this.filters.location !== 'all') ||
      this.filters.selectedAccountManagers.length > 0 ||
      this.activeTab !== 'all'
    )
  }

  get locationLabel() {
    const id = this.filters.location
    const location = (this.locations || []).find(l => l.id === id)

    return location?.name || 'Location'
  }

  managerLabel(manager) {
    return (
      manager.full_name ||
      manager.text ||
      [manager.first_name, manager.last_name].filter(Boolean).join(' ') ||
      'Account manager'
    )
  }

  setTab(value) {
    this.activeTab = value
    // Always re-arm: the loader is one shared component whose COMPLETE
    // status is not per-bucket, so a conditional bump can leave it wedged
    // COMPLETE for a bucket that still has pages left.
    this.infiniteId += 1
  }

  onTabHover(tab) {
    if (tab.value !== 'ai') return
    this.prefetchAiTab()
  }

  // `fetchTabPage`'s in-flight promise cache is the double-fetch guard if a
  // click follows this hover before the prefetch resolves. `aiPrefetchAttempted`
  // is a one-shot: a failed prefetch is not retried on every subsequent hover.
  prefetchAiTab() {
    if (!this.aiTextsEnabled) return
    if (this.buckets.ai.loaded || this.aiPrefetchAttempted) return

    this.aiPrefetchAttempted = true
    this.fetchTabPage('ai').catch(() => {})
  }

  resetLocation() {
    this.filters.location = 'all'
    this.search(true)
  }

  removeManager(manager) {
    this.filters.selectedAccountManagers =
      this.filters.selectedAccountManagers.filter(m => m.pkid !== manager.pkid)
    this.$refs?.accountManagers?.setValue?.(
      this.filters.selectedAccountManagers
    )
    this.search(true)
  }

  clearSearch() {
    this.filters.search = ''
    if (this.lastSearch) {
      this.search(true)
    }
  }

  onListKeydown(e) {
    if (!this.visibleMessages.length) return
    const key = e.key
    if (!['j', 'k', 'ArrowDown', 'ArrowUp', 'Enter'].includes(key)) return

    const idx = this.visibleMessages.findIndex(
      m => m.customer_id === this.activeContact
    )

    if (key === 'Enter' && idx >= 0) return

    e.preventDefault()
    let nextIdx = idx
    if (key === 'j' || key === 'ArrowDown') {
      nextIdx = Math.min(idx + 1, this.visibleMessages.length - 1)
    } else if (key === 'k' || key === 'ArrowUp') {
      nextIdx = Math.max(idx - 1, 0)
    }
    if (nextIdx === -1) nextIdx = 0
    const next = this.visibleMessages[nextIdx]
    if (next) this.getContact(next)
  }

  @Watch('customer')
  setTitle() {
    const crumb = [{ crumbTitle: 'Inbox' }]
    const fullName = this.customer?.first_name + ' ' + this.customer?.last_name
    const identifier = this.customer?.external_id ?? ' '

    if (this.filters.location) {
      const locationID = this.filters.location
      const currentLocation = this.locations.find(
        location => location.id === locationID
      )
      if (currentLocation?.id) {
        crumb.push({ crumbTitle: currentLocation.name })
      }
    }

    if (this.customer?.id) {
      crumb.push({
        crumbTitle: fullName + ' ' + identifier,
        directTo: 'CustomerAccountDetails'
      })

      this.$route.params.customerID = this.customer.id
    }

    this.$store.dispatch('setPageTitle', crumb)
  }

  async getCustomerInfo(params) {
    const customerInfo = await this.axios.post(
      this.$endpoints.listMerchantCustomerAccounts(
        this.$route.params.merchantID
      ),
      {
        params: {
          ...params
        }
      }
    )

    return customerInfo.data
  }

  compose() {
    this.getCustomerInfo().then(res => {
      res.results = res.results.map(item => {
        const customerName = item.first_name + ' ' + item.last_name

        return {
          name: customerName,
          email: item.customer.email,
          phone: item.customer.sms_phone_number,
          text: customerName,
          customerID: item.customer.id,
          accountID: item.account.id
        }
      })
      this.customers = res.results
      this.$bvModal.show('compose-message-modal')
    })
  }

  destroyed() {
    clearTimeout(this.chatTimerId)
    this.$store.commit('clearCurrentCustomer')
    this.$store.commit('clearCurrentAccount')

    this.$eventBus.off('chat', this.onChat)
    document.removeEventListener('click', this.onOutsideFilterClick, true)
  }

  async onChat(chat) {
    const id = chat.customerId

    let contact
    try {
      // Not scoped to the active tab: this single fetch may need to update
      // both buckets (see below), so it must succeed regardless of which
      // tab is active rather than 404 under an `ai_texts` filter.
      const response = await this.axios.get(
        this.$endpoints.retrieveInbox(this.merchant.id, id),
        { params: buildInboxFilterParams(this.filters) }
      )
      contact = response.data
    } catch (error) {
      if (error?.response?.status === 404) return
      throw error
    }

    if (!contact) return

    // `all` is the master list: always upsert (insert-or-reposition), regardless of which tab is active,
    // so a brand-new conversation still surfaces there.
    upsertInboxConversation(this.buckets.all.rows, contact)

    const aiRows = this.buckets.ai.rows
    const aiIdx = aiRows.findIndex(r => r.customer_id === contact.customer_id)
    if (aiIdx !== -1) {
      this.$set(aiRows, aiIdx, { ...aiRows[aiIdx], ...contact })
    }
  }

  getContact(messageCustomer) {
    const customerId = messageCustomer.customer_id
    this.activeContact = customerId
    this.activeContactServerKey = serverKeyForTab(this.activeTab)

    // The same customer can have a distinct row object in each bucket;
    // clear unread on every copy so the Unread badge (which reads only the
    // `all` bucket) doesn't stay stale after reading from the `ai` tab.
    Object.values(this.buckets).forEach(bucket => {
      const row = bucket.rows.find(r => r.customer_id === customerId)
      if (row) row.unread_messages = 0
    })

    this.getCustomer({ customerID: customerId, checkIntegration: false })
  }

  search(force = false) {
    // this triggers the load() function, that does the actual search
    if (!this.filters.search && !this.lastSearch && !force) return
    this.lastSearch = this.filters.search
    this.clear()
  }

  onResetFilters() {
    this.filters.location = 'all'
    this.filters.search = ''
    this.filters.selectedField = this.defaultField
    this.filters.selectedAccountManagers = []
    this.activeTab = 'all'
    this.$refs?.accountManagers?.clear?.()
    this.setTitle()
    this.search(true)
  }

  // Clears the draft only — `filters` (and the applied manager chips) are
  // untouched until Apply.
  clearDraftAccountManagers() {
    this.draftManagers = []
    this.$refs?.accountManagers?.clear()
  }

  setSearchField(field) {
    this.filters.selectedField = field
  }

  // Shared by the infinite-loader (`load`) and `prefetchAiTab`. Takes `tab`
  // rather than reading `this.activeTab` because prefetch fires before the
  // tab is active.
  fetchTabPage(tab) {
    const serverKey = serverKeyForTab(tab)
    const bucket = this.buckets[serverKey]

    if (bucket.complete) return Promise.resolve(bucket)

    return inFlight(this.pendingFetch, serverKey, () =>
      this.axios
        .get(this.$endpoints.listInbox(this.merchant.id), {
          params: {
            cursor: bucket.cursor,
            page_size: this.page_size,
            ...buildInboxFilterParams(this.filters, tab)
          }
        })
        .then(response => {
          Object.assign(
            bucket,
            nextBucketState(bucket, {
              results: response?.data?.results,
              next: response?.data?.next
            })
          )

          return bucket
        })
    )
  }

  // `state` is the single InfiniteLoading instance's shared state object — it is not re-created when a
  // tab switch bumps `infiniteId` and resets it.
  async load(state) {
    const requestKey = serverKeyForTab(this.activeTab)

    try {
      const bucket = await this.fetchTabPage(this.activeTab)

      if (serverKeyForTab(this.activeTab) !== requestKey) return

      // Only the bucket the open conversation was selected from should be able to close it — otherwise
      // switching to a tab that simply doesn't carry that customer (e.g. AI, for a customer with no AI
      // history) would incorrectly collapse the chat pane.
      if (requestKey === this.activeContactServerKey) {
        const stillActive = bucket.rows.find(
          c => c.customer_id === this.activeContact
        )
        if (!stillActive) {
          this.activeContact = null
          this.activeContactServerKey = null
          this.$store.commit('clearCurrentCustomer')
        }
      }

      this.initialLoading = false
      state.loaded()
      if (bucket.complete) {
        state.complete()
      }
      this.setTitle()
    } catch {
      if (serverKeyForTab(this.activeTab) !== requestKey) return

      this.initialLoading = false
      state.complete()
    }
  }

  clear() {
    // Filter change: invalidate both buckets (location/search/account
    // managers apply to both); a tab switch alone must not call this.
    this.buckets = invalidateInboxBuckets()
    // Reset so a stale in-flight request isn't mistaken for one against the
    // new buckets.
    this.pendingFetch = { all: null, ai: null }
    this.aiPrefetchAttempted = false
    this.initialLoading = true
    this.infiniteId += 1
  }

  accountID() {
    return this.currentAccount?.id
  }

  get currentAccount() {
    const accounts = this.customer?.accounts || []
    if (!accounts.length) return null
    if (this.selectedAccountId) {
      const match = accounts.find(a => a.id === this.selectedAccountId)
      if (match) return match
    }

    return accounts[0]
  }

  @Watch('customer.id')
  onCustomerChange() {
    this.selectedAccountId = null
  }

  onAccountChange(id) {
    this.selectedAccountId = id
  }

  initials(customerMessage) {
    const initials =
      customerMessage?.first_name[0] + customerMessage?.last_name[0]

    return initials.toUpperCase()
  }

  name(messageCustomer) {
    return messageCustomer?.first_name + ' ' + messageCustomer?.last_name
  }

  // `#filter-trigger-btn` is also the popover's own `triggers="click"` target, so a second click
  // (closing it) reaches this handler too; the guard keeps that click a no-op here and lets
  // bootstrap-vue's own toggle close it, instead of re-seeding and re-opening over an in-progress edit.
  displayFilters() {
    if (this.showFilterPopover) return

    const draft = copyLocationFilterState(this.filters)
    this.draftLocation = draft.location
    this.draftManagers = draft.selectedAccountManagers
    this.showFilterPopover = true
  }

  // Unlike the old modal, the popover's slot isn't mounted while hidden, so `$refs.accountManagers` only
  // exists once `@shown` fires.
  onFilterPopoverShown() {
    if (this.$refs.accountManagers) {
      this.$refs.accountManagers.setValue(this.draftManagers)
    }
    document.addEventListener('click', this.onOutsideFilterClick, true)
  }

  onFilterPopoverHidden() {
    document.removeEventListener('click', this.onOutsideFilterClick, true)
  }

  // `triggers="click"` alone only closes the popover from a second click on its own target;
  // bootstrap-vue's `blur` trigger only watches the target losing focus, which never fires again once
  // focus has moved into the popover's own form controls.
  onOutsideFilterClick(event) {
    const trigger = document.getElementById('filter-trigger-btn')
    if (trigger?.contains(event.target)) return
    if (event.target.closest?.('.b-popover')) return

    this.cancelFilters()
  }

  // Replaces the old modal's `@ok="clear"`; `clear()` still does the right thing
  // post-filter-change.
  applyFilters() {
    const next = copyLocationFilterState({
      location: this.draftLocation,
      selectedAccountManagers: this.draftManagers
    })
    this.filters.location = next.location
    this.filters.selectedAccountManagers = next.selectedAccountManagers
    this.showFilterPopover = false
    this.setTitle()
    this.clear()
  }

  // Called directly by Cancel and by `onOutsideFilterClick` (the click-away handler):
  // neither path commits through `applyFilters`, so `filters` is untouched and
  // click-away behaves like Cancel by construction.
  cancelFilters() {
    this.showFilterPopover = false
  }

  get locationList() {
    const locations = this?.locations || []
    const normalizedList = locations.map(location => {
      return {
        value: location.id,
        text: location.name
      }
    })
    normalizedList.unshift({
      value: 'all',
      text: 'All Locations'
    })

    return normalizedList
  }

  // ModelSelect binds the whole `{ value, text }` option; the draft (and `filters`) hold the bare
  // id, so translate in both directions rather than changing what the filter state stores.
  get draftLocationOption() {
    return (
      this.locationList.find(option => option.value === this.draftLocation) ||
      this.locationList[0]
    )
  }

  set draftLocationOption(option) {
    this.draftLocation = option?.value ?? 'all'
  }

  // Sets the draft, not `filters`.
  onDraftAccountManager(accountManagers) {
    this.draftManagers = accountManagers || []
  }

  get canComposeMassMessage() {
    if (this.$store.getters.currentMerchant.check_custom_roles) {
      return this.hasPermission([
        'super_admin',
        'admin',
        'is_customer_campaign_allowed'
      ])
    }

    return this.hasPermission(['super_admin', 'admin', 'staff'])
  }
}
</script>

<style lang="scss">
@import '@/scss/variables';

.inbox-filters-popover {
  min-width: 300px;

  .filter-model-select.ui.selection.dropdown,
  .ti-input {
    min-height: 36px;
    padding: 0 10px;
    color: $blue;

    font-size: 14px;

    background-color: #f5f7fa;
    border: 1px solid transparent !important;
    border-radius: 8px !important;
    transition:
      background-color 0.15s,
      border-color 0.15s;
  }

  // The whole point of swapping the native select out: an OS-drawn option list ignores CSS, a
  // ModelSelect menu is real DOM.
  .filter-model-select.ui.selection.dropdown {
    display: flex;
    align-items: center;
    padding-right: 28px;
    line-height: 1;

    &.active,
    &:focus {
      background-color: #fff;
      border-color: $button-blue !important;
      box-shadow: none;
    }

    > .text {
      color: $blue;
    }

    > .default.text {
      color: $darker-gray;
    }

    // Mirrors .ti-autocomplete on the account-manager field below it (MultiUserSelect.vue)
    .menu {
      max-height: 150px;
      margin-top: 0;
      color: #212529;

      background-color: #fff !important;
      border: 1px solid #ced4da !important;
      border-top: none !important;
      border-radius: 0 0 8px 8px !important;
      box-shadow: none !important;

      > .item {
        padding: 6px 10px !important;
        color: #212529;

        font-size: 1rem;
        line-height: 1.5;
        border-top: none !important;

        &:hover,
        &.selected,
        &.active.selected {
          color: #fff;

          background-color: $button-blue !important;
        }
      }
    }
  }

  .ti-input:focus-within {
    background-color: #fff;
    border-color: $button-blue !important;
    box-shadow: none;
    outline: none;
  }

  .form-group > label {
    margin-bottom: 4px;
    color: $blue;

    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
  }

  .form-group {
    margin-bottom: 12px;
  }
}
</style>

<style lang="scss" scoped>
@import '@/scss/variables';

#inbox-container {
  width: 100%;
  height: 100%;
  background: #fff;
}

#contact-list {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  width: 40vw;
  min-width: 320px;
  max-width: 520px;
  height: 100%;
  background: #fff;
  border-right: 1px solid #e5e9f0;
}

.list-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 16px 0;
  border-bottom: 1px solid #eef1f5;
}

.compose-btn {
  display: inline-flex;
  flex-shrink: 0;
  gap: 6px;
  align-items: center;

  font-size: 12px;
  font-weight: 600;
  border-radius: 8px;
}

.search-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.search-field {
  display: flex;
  flex: 1;
  align-items: center;
  padding: 0 10px;

  background: #f5f7fa;
  border: 1px solid transparent;
  border-radius: 8px;
  transition:
    border-color 0.15s,
    background 0.15s;

  &:focus-within {
    background: #fff;
    border-color: $lighter-blue;
  }

  .search-glyph {
    flex-shrink: 0;
    color: $darker-gray;
  }

  #search-inbox-input {
    height: 36px;
    padding: 0 8px;
    color: $blue;

    font-size: 15px;

    background: transparent;
    border: 0;
    box-shadow: none;

    &:focus {
      outline: none;
      box-shadow: none;
    }
  }
}

.clear-search {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  color: $darker-gray;

  background: transparent;
  border: 0;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    color: $blue;

    background: rgba(0, 0, 0, 0.05);
  }
}

.field-dropdown {
  ::v-deep .btn {
    font-size: 12px;
    border-radius: 8px;
  }

  ::v-deep .dropdown-item {
    &.active,
    &:active {
      background-color: $button-blue;
    }
  }
}

.tab-row {
  display: flex;
  // Three items share this row and #contact-list bottoms out at 320px; wrapping drops the
  // trailing button to its own line rather than crushing the pills.
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.tab-strip {
  display: flex;
  gap: 4px;
  margin: 0 -4px;
}

.tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 14px;
  color: $darker-gray;

  font-size: 14px;
  font-weight: 600;
  line-height: 1;

  background: transparent;
  border: 1px solid transparentize($darker-gray, 0.55);
  border-radius: 999px;
  cursor: pointer;
  transition:
    color 0.15s,
    background-color 0.15s,
    border-color 0.15s;

  &:hover {
    color: $blue;

    border-color: $button-blue;
  }

  &.active {
    color: #fff;

    background: $button-blue;
    border-color: $button-blue;
  }

  .tab-count {
    color: $darker-gray;

    font-size: 11px;
    font-weight: 600;
    line-height: 1;
  }

  // Full white, not a softened one: anything below ~90% alpha drops under the 4.5:1 AA floor
  // against $button-blue. The 11px/14px size gap carries the hierarchy instead.
  &.active .tab-count {
    color: #fff;
  }
}

.chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-bottom: 12px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  color: $blue;

  font-size: 12px;
  font-weight: 500;

  background: #eef3f9;
  border: 1px solid transparent;
  border-radius: 999px;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover {
    background: #e1eaf3;
  }

  .chip-x {
    margin-left: 2px;
    color: $darker-gray;
  }
}

.chip-add {
  flex-shrink: 0;
  // Starts the right-hand group in `.tab-row` (this chip, then Group message).
  margin-left: auto;
  color: $darker-gray;

  background: #fff;
  border: 1px dashed #cdd5df;

  &:hover {
    color: $blue;

    background: #f5f7fa;
    border-color: $lighter-blue;
  }
}

.chip-clear {
  color: $darker-gray;

  background: transparent;

  &:hover {
    color: $red;

    background: transparent;
  }
}

.list-scroll {
  position: relative;

  flex: 1;
  min-height: 0;
  overflow: auto;
  outline: none;
}

.skeleton-overlay {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;

  padding: 8px 0;

  background: #fff;
  pointer-events: none;
}

.relative-parent {
  position: relative;

  padding: 4px 0 24px;
}

.date-header {
  padding: 14px 16px 6px;
  color: $darker-gray;

  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.contact-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;

  text-align: left;

  background: #fff;
  border: 0;
  border-left: 3px solid transparent;
  cursor: pointer;
  transition:
    background 0.12s,
    border-color 0.12s;

  &:hover {
    background: #f5f8fb;
  }

  &.active {
    background: lighten($lighter-blue, 28%);
    border-left-color: $bright-blue;

    .name,
    .preview-body {
      color: $blue;
    }
  }

  &.unread {
    .name {
      color: $blue;

      font-weight: 700;
    }

    .preview-body {
      color: $blue;

      font-weight: 500;
    }
  }
}

.avatar-wrap {
  position: relative;

  flex-shrink: 0;
}

.presence-dot {
  position: absolute;
  top: -2px;
  right: -2px;

  width: 10px;
  height: 10px;

  background: $bright-blue;
  border: 2px solid #fff;
  border-radius: 50%;
}

.preview {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.row-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.name {
  overflow: hidden;
  color: $blue;

  font-size: 15px;
  font-weight: 500;

  white-space: nowrap;
  text-overflow: ellipsis;
}

.time {
  flex-shrink: 0;
  color: $darker-gray;

  font-size: 12.5px;
  white-space: nowrap;
}

.row-bottom {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}

.ext-id {
  flex-shrink: 0;
  color: $darker-gray;

  font-size: 12.5px;
  font-weight: 600;
}

.ext-id::after {
  margin-left: 4px;

  content: '·';
}

.preview-body {
  overflow: hidden;
  color: $darker-gray;

  font-size: 13.5px;

  white-space: nowrap;
  text-overflow: ellipsis;
}

.ellipsis {
  overflow: hidden;

  white-space: nowrap;
  text-overflow: ellipsis;
}

.contact-enter,
.contact-leave-to {
  opacity: 0;
}

.contact-leave-active {
  position: absolute;

  width: 100%;
}

.footer-state {
  padding: 24px 16px;
  color: $darker-gray;

  font-size: 13px;
  text-align: center;

  &.empty {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    padding-top: 48px;

    svg {
      color: lighten($darker-gray, 15%);
    }

    p {
      margin: 0;
    }
  }
}

.link-btn {
  padding: 4px 10px;
  color: $bright-blue;

  font-size: 12px;
  font-weight: 600;

  background: transparent;
  border: 0;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: lighten($bright-blue, 42%);
  }
}

.skeleton-row {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px 16px;
}

.skel {
  background: linear-gradient(90deg, #eef1f5 0%, #f7f9fc 50%, #eef1f5 100%);
  background-size: 200% 100%;
  border-radius: 6px;
  animation: shimmer 1.4s linear infinite;
}

.skel-avatar {
  width: 36px;
  height: 36px;

  border-radius: 50%;
}

.skel-lines {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 6px;
}

.skel-line {
  width: 80%;
  height: 10px;

  &.short {
    width: 55%;
    height: 8px;
  }
}

@keyframes shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

#inbox-chat-placeholder,
.chat-empty {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 100%;

  background: #fafbfd;
  border-left: 0;
}

.empty-inner {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  max-width: 360px;

  text-align: center;
}

.empty-art {
  width: 160px;
  margin-bottom: 8px;
  opacity: 0.85;
}

.empty-inner h2 {
  margin: 0;
  color: $blue;

  font-size: 1.1rem;
  font-weight: 600;
}

.empty-inner p {
  margin: 0;
  color: $darker-gray;

  font-size: 13px;
}

// Filter popover
u {
  cursor: pointer;
  white-space: nowrap;
}

.h-flex.vertical-center {
  justify-content: flex-start !important;
  gap: 0.75rem;
}

.vue-tags-input {
  max-width: 100% !important;
  border-radius: 0.25rem;

  ::v-deep input {
    cursor: pointer;
  }
}

.popover-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 12px;
}
</style>
<style src="vue-search-select/dist/VueSearchSelect.css" />
